import React, { useEffect, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore, collection, getDocs, query, where, doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import app, { professorAuth } from "../../firebase";

const auth = professorAuth;
const db = getFirestore(app);

function buildTermKey({ schoolYear, semester, department, yearLevel, section }) {
  return [schoolYear, semester, department, yearLevel, section]
    .map((value) => String(value || "").trim().replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, ""))
    .join("__");
}

export default function ProfessorGrading() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profName, setProfName] = useState("Professor");
  const [profEmail, setProfEmail] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [filterSchoolYear, setFilterSchoolYear] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterYearLevel, setFilterYearLevel] = useState("");
  const [filterSemester, setFilterSemester] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [students, setStudents] = useState([]);
  const [studentGrades, setStudentGrades] = useState({});

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/academics");
        return;
      }

      setProfEmail(user.email || "");
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setProfName(userSnap.data().name || "Professor");
        }
      } catch (err) {
        console.error("Failed to load professor profile:", err);
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (!profEmail) return;

    const fetchSchedules = async () => {
      try {
        const schedulesRef = collection(db, "schedules");
        // Primary: query schedules where canonical `prof` (email) was saved
        const q = query(schedulesRef, where("prof", "==", profEmail));
        const snap = await getDocs(q);
        let scheduleRows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // If nothing matched, broaden client-side by fetching recent schedules and
        // matching against several possible professor identifiers stored in documents
        if (scheduleRows.length === 0) {
          const allSnap = await getDocs(schedulesRef);
          const lowerEmail = profEmail.toLowerCase();
          const lowerName = String(profName || "").toLowerCase();
          scheduleRows = allSnap.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((sched) => {
              const profFields = [sched.prof, sched.professorEmail, sched.professor, sched.professorId]
                .filter(Boolean)
                .map(v => String(v).toLowerCase());
              // match email anywhere
              if (profFields.some(f => f.includes(lowerEmail))) return true;
              // match by name if available
              if (String(sched.professor || "").toLowerCase().includes(lowerName)) return true;
              return false;
            });
        }

        setSchedules(scheduleRows);
      } catch (err) {
        console.error("Failed to load schedules for grading:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [profEmail, profName]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/academics");
  };

  const schoolYears = Array.from(new Set(schedules.map((row) => row.schoolYear).filter(Boolean))).sort();
  const departments = Array.from(new Set(schedules.map((row) => row.department).filter(Boolean))).sort();
  const yearLevels = Array.from(new Set(schedules.map((row) => row.yearLevel).filter(Boolean))).sort();
  const semesters = Array.from(new Set(schedules.map((row) => row.semester).filter(Boolean))).sort();
  const sections = Array.from(new Set(schedules.map((row) => row.section).filter(Boolean))).sort();

  const filteredSchedules = schedules.filter((row) => {
    return (
      (!filterSchoolYear || row.schoolYear === filterSchoolYear) &&
      (!filterDepartment || row.department === filterDepartment) &&
      (!filterYearLevel || row.yearLevel === filterYearLevel) &&
      (!filterSemester || row.semester === filterSemester) &&
      (!filterSection || row.section === filterSection)
    );
  });

  const courseOptions = Array.from(new Set(filteredSchedules.map((row) => row.courseId).filter(Boolean))).sort();
  const selectedCourseName = filteredSchedules.find((row) => row.courseId === selectedCourse)?.courseName || "";

  useEffect(() => {
    if (!filterSchoolYear || !filterDepartment || !filterYearLevel || !filterSemester || !filterSection || !selectedCourse) {
      setStudents([]);
      setStudentGrades({});
      return;
    }

    const fetchStudents = async () => {
      try {
        const studentsRef = collection(db, "students");
        // Primary: query students by program to limit reads
        const q = query(studentsRef, where("program", "==", filterDepartment));
        let snapshot = await getDocs(q);
        let fetched = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const termKey = buildTermKey({ schoolYear: filterSchoolYear, semester: filterSemester, department: filterDepartment, yearLevel: filterYearLevel, section: filterSection });

        const filterByPreReg = (student) => {
          const yearLevel = student.preRegistration?.entryLevel || student.yearLevel || "";
          const semester = student.preRegistration?.semester || "";
          const section = student.preRegistration?.section || "";
          return yearLevel === filterYearLevel && semester === filterSemester && section === filterSection;
        };

        let filtered = fetched.filter(filterByPreReg);

        // If primary method found none, try fallbacks using termSchedules or academic records
        if (filtered.length === 0) {
          console.log("ProfessorGrading: no students via preRegistration, trying termSchedules/grade fallbacks");
          // Broaden: fetch all students (expensive) and filter client-side by termSchedules or academicGrades
          const allSnap = await getDocs(studentsRef);
          fetched = allSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

          const lowerProfEmail = String(profEmail || "").toLowerCase();
          const lowerProfName = String(profName || "").toLowerCase();

          filtered = fetched.filter((student) => {
            // 1) Check termSchedules for this termKey
            const termSchedules = student.termSchedules?.[termKey];
            if (termSchedules) {
              // termSchedules may be an array or an object keyed by courseId
              if (Array.isArray(termSchedules)) {
                if (termSchedules.some(ts => ts.courseId === selectedCourse && (
                  String(ts.prof || ts.professorEmail || ts.professor || "").toLowerCase().includes(lowerProfEmail) ||
                  String(ts.professor || "").toLowerCase().includes(lowerProfName)
                ))) return true;
              } else if (typeof termSchedules === 'object') {
                const entry = termSchedules[selectedCourse] || Object.values(termSchedules).find(v => v.courseId === selectedCourse);
                if (entry && (
                  String(entry.prof || entry.professorEmail || entry.professor || "").toLowerCase().includes(lowerProfEmail) ||
                  String(entry.professor || "").toLowerCase().includes(lowerProfName)
                )) return true;
              }
            }

            // 2) Check academicGrades metadata for this term/course
            const gradeEntry = student.academicGrades?.[termKey]?.[selectedCourse];
            if (gradeEntry) {
              if (String(gradeEntry.professorEmail || gradeEntry.professor || "").toLowerCase().includes(lowerProfEmail)) return true;
            }

            // 3) Fallback: match by preRegistration fields (already covered) or program/section heuristics
            return false;
          });
        }

        setStudents(filtered);
        const gradesMap = {};
        filtered.forEach((student) => {
          const stored = student.academicGrades?.[termKey]?.[selectedCourse];
          gradesMap[student.id] = stored?.grade || "";
        });
        setStudentGrades(gradesMap);
      } catch (err) {
        console.error("Failed to load students for grading:", err);
      }
    };

    fetchStudents();
  }, [filterSchoolYear, filterDepartment, filterYearLevel, filterSemester, filterSection, selectedCourse, profEmail, profName]);

  const handleGradeChange = (studentId, value) => {
    setStudentGrades((prev) => ({ ...prev, [studentId]: value }));
  };

  const handleSaveGrades = async () => {
    if (!selectedCourse || !filterSchoolYear || !filterDepartment || !filterYearLevel || !filterSemester || !filterSection) {
      alert("Please select all filters and a course before saving grades.");
      return;
    }

    setSaving(true);
    try {
      const termKey = buildTermKey({ schoolYear: filterSchoolYear, semester: filterSemester, department: filterDepartment, yearLevel: filterYearLevel, section: filterSection });
      const updates = students.map(async (student) => {
        const studentRef = doc(db, "students", student.id);
        const gradeValue = studentGrades[student.id] || "";
        await updateDoc(studentRef, {
          [`academicGrades.${termKey}.${selectedCourse}`]: {
            grade: gradeValue,
            courseName: selectedCourseName,
            professor: profName,
            professorEmail: profEmail,
            updatedAt: new Date().toISOString(),
            schoolYear: filterSchoolYear,
            semester: filterSemester,
            department: filterDepartment,
            yearLevel: filterYearLevel,
            section: filterSection,
          },
        });
      });
      await Promise.all(updates);
      alert("Grades saved successfully.");
    } catch (err) {
      console.error("Failed to save grades:", err);
      alert("There was an error saving grades. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
      <div style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
        <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
        <button onClick={() => navigate('/professor')} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Home</button>
        <button onClick={() => navigate('/professor/schedule')} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Schedule</button>
        <button onClick={() => navigate('/professor/grading')} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, boxShadow: "0 2px 8px #e67e22aa" }}>Grading</button>
        <div style={{ flex: 1 }} />
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <div style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", justifyContent: "space-between" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Professor Grading</h1>
            <div style={{ color: "#4a5568", marginTop: 4 }}>{profName}</div>
          </div>
          <button onClick={handleLogout} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontWeight: 700, cursor: "pointer" }}>Log out</button>
        </div>

        <div style={{ flex: 1, padding: 40, overflowY: "auto" }}>
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 16, marginBottom: 22 }}>
              <select value={filterSchoolYear} onChange={(e) => setFilterSchoolYear(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">School Year</option>
                {schoolYears.map((schoolYear) => <option key={schoolYear} value={schoolYear}>{schoolYear}</option>)}
              </select>
              <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Department</option>
                {departments.map((department) => <option key={department} value={department}>{department}</option>)}
              </select>
              <select value={filterYearLevel} onChange={(e) => setFilterYearLevel(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Year Level</option>
                {yearLevels.map((yearLevel) => <option key={yearLevel} value={yearLevel}>{yearLevel}</option>)}
              </select>
              <select value={filterSemester} onChange={(e) => setFilterSemester(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Semester</option>
                {semesters.map((semester) => <option key={semester} value={semester}>{semester}</option>)}
              </select>
              <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Section</option>
                {sections.map((section) => <option key={section} value={section}>{section}</option>)}
              </select>
              <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} style={{ padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 }}>
                <option value="">Course</option>
                {courseOptions.map((courseId) => <option key={courseId} value={courseId}>{courseId}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ color: "#4a5568" }}>
                {selectedCourse ? `Entering grades for ${selectedCourse}${selectedCourseName ? ` — ${selectedCourseName}` : ""}` : "Select a course to begin grading."}
              </div>
              <button onClick={handleSaveGrades} disabled={saving || students.length === 0 || !selectedCourse} style={{ background: saving ? "#94a3b8" : "#155c2b", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
                {saving ? "Saving grades..." : "Save grades"}
              </button>
            </div>

            <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
              {loading ? (
                <div style={{ color: "#718096" }}>Loading grading data...</div>
              ) : !selectedCourse ? (
                <div style={{ color: "#718096" }}>Choose a term, section, and course to load your assigned students.</div>
              ) : students.length === 0 ? (
                <div style={{ color: "#718096" }}>No students found for the selected term and section.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f7faf7" }}>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>ID Number</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Name</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Program</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Section</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", fontWeight: 700, color: "#334155" }}>Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id}>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{student.idNumber || student.studentId || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{student.lastName}, {student.firstName}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{student.program || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{student.preRegistration?.section || student.section || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>
                            <input
                              value={studentGrades[student.id] || ""}
                              onChange={(e) => handleGradeChange(student.id, e.target.value)}
                              placeholder="Enter grade"
                              style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
